import * as React from "react";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, Trash2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildAcceptFromConstraints, DEFAULT_CONSTRAINTS, Props, ValidationIssue } from "./types/type";

const keyOf = (f: File) => `${f.name}__${f.size}__${f.lastModified}`;

function mergeFiles(existing: File[], incoming: File[]) {
  const map = new Map<string, File>();
  existing.forEach((f) => map.set(keyOf(f), f));
  incoming.forEach((f) => map.set(keyOf(f), f));
  return Array.from(map.values());
}

function formatBytes(bytes: number) {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`;
}

function isAllowedType(f: File, acceptMime?: string[], acceptExt?: string[]) {
  const okMime = !acceptMime?.length || acceptMime.includes(f.type);
  const lower = f.name.toLowerCase();
  const okExt = !acceptExt?.length || acceptExt.some((ext) => lower.endsWith(ext.toLowerCase()));
  return okMime || okExt; // igual ao backend: passa se MIME OU extensão
}

export const InputMultiPdfUpload: React.FC<Props> = (props) => {
  const {
    files,
    onChange,
    disabled,
    onClear,
    appendOnSelect = true,
    footer,
    onValidationError,
  } = props;

  // se o caller NÃO passou, usamos defaults centralizados
  const constraints = props.constraints ?? DEFAULT_CONSTRAINTS;
  // se o caller NÃO passou accept, derivamos das constraints (para evitar divergência)
  const accept = props.accept ?? buildAcceptFromConstraints(constraints);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const emitIssues = (issues: ValidationIssue[]) => {
    onValidationError?.(issues);
  };

  const handlePickClick = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const validatePicked = (picked: File[]): { valid: File[]; issues: ValidationIssue[] } => {
    const issues: ValidationIssue[] = [];
    const validByTypeSize = picked.filter((f) => {
      if (!isAllowedType(f, constraints.acceptMime, constraints.acceptExt)) {
        issues.push({ code: "type", message: "Apenas PDFs são permitidos.", fileName: f.name, fileSize: f.size });
        return false;
      }
      if (constraints.maxFileSizeBytes && f.size > constraints.maxFileSizeBytes) {
        issues.push({
          code: "size",
          message: `Arquivo excede o limite de ${formatBytes(constraints.maxFileSizeBytes)}.`,
          fileName: f.name,
          fileSize: f.size,
        });
        return false;
      }
      return true;
    });

    const merged = appendOnSelect ? mergeFiles(files, validByTypeSize) : validByTypeSize;

    let finalList = merged;
    if (constraints.maxFiles && merged.length > constraints.maxFiles) {
      finalList = merged.slice(0, constraints.maxFiles);
      issues.push({ code: "count", message: `Limite de ${constraints.maxFiles} arquivo(s) atingido.` });
    }
    return { valid: finalList, issues };
  };

  const applySelection = (picked: File[]) => {
    const { valid, issues } = validatePicked(picked);
    if (issues.length) emitIssues(issues);
    onChange(valid);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files ? Array.from(event.target.files) : [];
    if (picked.length) applySelection(picked);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    if (dropped.length) applySelection(dropped);
  };

  const removeOne = (file: File) => {
    const k = keyOf(file);
    onChange(files.filter((f) => keyOf(f) !== k));
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-[#613F4F]" />
            <CardTitle className="text-lg text-[#613F4F]">Upload de Arquivos PDF</CardTitle>
          </div>

          {!!files.length && onClear && (
            <Button
              variant="outline"
              size="sm"
              className={cn("border-[#613F4F] text-[#613F4F]", "hover:bg-[#613F4F]/5")}
              onClick={onClear}
              disabled={disabled}
              title="Limpar selecionados"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Limpar selecionados
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Dropzone */}
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
            files.length ? "border-[#F6D040] bg-[#F6D040]/5" : "border-gray-300 hover:border-[#F6D040] hover:bg-[#F6D040]/5",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={handlePickClick}
        >
          <p className="text-gray-700">{files.length ? "Arquivos selecionados:" : "Arraste e solte seus PDFs aqui ou clique"}</p>

          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Lista de arquivos */}
        {!!files.length && (
          <ul className={cn("max-h-56 overflow-auto divide-y rounded-md border bg-white")}>
            {files.map((f) => (
              <li key={keyOf(f)} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-[#613F4F]">{f.name}</p>
                  <p className="text-xs text-gray-500">{formatBytes(f.size)}</p>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className={cn("h-8 w-8 text-gray-500 hover:text-[#613F4F]")}
                  onClick={() => removeOne(f)}
                  disabled={disabled}
                  aria-label={`Remover ${f.name}`}
                  title="Remover"
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        {footer}
      </CardContent>
    </Card>
  );
};
